/**
 * PAGE: LISTE FORMULAIRES (VERSION REFACTORISÉE)
 */

import type { ColumnDef } from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';
import { ColumnSelectorModal, FiltersModal, ImportCSVModal } from '../components/modals';
import { CreateFormModal } from '../components/modals/CreateFormModal';
import { FormDetailPanel } from '../components/panels/FormDetailPanel';
import { ActionButtons, PageContent, PageHeader, PageLayout, Pagination } from '../components/page';
import { EnterpriseTable } from '../components/tables';
import { CheckboxCell, ActionsCell, EmptyCell } from '../components/tables/cells';
import { TableCell } from '../components/table';
import { Badge, BulkActionsBar, ConfirmDialog, ErrorState, LoadingState } from '../components/ui';
import { useEntityList } from '../hooks/useEntityList';
import { useForms } from '../hooks/useForms';
import { useSupabase } from '../hooks/useSupabase';
import { useOpenFromSearch } from '../hooks/useOpenFromSearch';
import { useOrigins } from '../hooks/useOrigins';
import { usePermissions } from '../hooks/usePermissions';
import { useProducts } from '../hooks/useProducts';
import type { Form } from '../types/form';
import { FORM_CONFIG } from '../config/entities/form.config';
import {
  generateCSVColumns,
  generateCSVTransformers,
  generateImportConfig,
  generateFilterConfigs,
  generateColumnOptions,
  generateInitialColumnVisibility,
} from '../lib/entity-config';

export function FormsListPage() {
  const supabase = useSupabase();
  const {
    data: forms,
    loading,
    error,
    create,
    update,
    remove,
    removeMany,
    refreshForms,
  } = useForms();
  const { origins } = useOrigins();
  const { products } = useProducts();

  // Permissions utilisateur
  const { canCreateEntity, canDeleteEntity, canImportData, canExportData, canDeleteBulk } =
    usePermissions();

  const [selectedForm, setSelectedForm] = useState<Form | null>(null);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);

  const handleCloseSidePanel = useCallback(() => {
    setIsSidePanelOpen(false);
    setSelectedForm(null);
  }, []);

  // Générer automatiquement depuis FORM_CONFIG + custom filters pour origin et product
  const filterConfigs = useMemo(() => {
    const base = generateFilterConfigs(FORM_CONFIG);
    return [
      ...base,
      {
        field: 'product.name',
        label: 'Produit',
        type: 'select' as const,
        operators: [
          'equals' as const,
          'notEquals' as const,
          'isNotNull' as const,
          'isNull' as const,
        ],
        options: products.map((product) => ({
          value: product.name,
          label: product.name,
        })),
      },
      {
        field: 'origin.label',
        label: 'Origine',
        type: 'select' as const,
        operators: [
          'equals' as const,
          'notEquals' as const,
          'isNotNull' as const,
          'isNull' as const,
        ],
        options: origins.map((origin) => ({
          value: origin.label,
          label: origin.label,
        })),
      },
    ];
  }, [origins, products]);

  const csvColumns = useMemo(() => generateCSVColumns(FORM_CONFIG), []);
  const csvTransformers = useMemo(() => generateCSVTransformers(FORM_CONFIG), []);
  const importConfig = useMemo(() => generateImportConfig(FORM_CONFIG), []);
  const initialColumnVisibility = useMemo(() => generateInitialColumnVisibility(FORM_CONFIG), []);

  const entity = useEntityList({
    data: forms,
    loading,
    error,
    create,
    remove,
    removeMany,
    refresh: refreshForms,
    entityName: FORM_CONFIG.entityName,
    entityNamePlural: FORM_CONFIG.entityNamePlural,
    searchableFields: ['name'] as (keyof Form)[],
    filterConfigs,
    initialColumnVisibility, // ✅ Visibilité initiale des colonnes
    csvFileName: 'formulaires.csv',
    csvColumns,
    csvTransformers,
    importConfig,
    onDeleteSuccess: handleCloseSidePanel,
  });

  const { handleDeleteClick } = entity;

  const handleOpenSidePanel = useCallback((form: Form) => {
    setSelectedForm(form);
    setIsSidePanelOpen(true);
  }, []);

  // Ouvrir automatiquement le side panel depuis la recherche globale
  useOpenFromSearch({
    data: forms,
    getId: (f) => f.id,
    onOpen: handleOpenSidePanel,
    loading,
  });

  const handleUpdateForm = useCallback(
    async (id: string, data: any) => {
      if (!update) return;

      try {
        await update(id, data);
        // Rafraîchir la liste pour récupérer les relations mises à jour
        const freshForms = await refreshForms();
        // Mettre à jour le selectedForm avec les nouvelles données fraîches
        if (selectedForm?.id === id) {
          const updatedForm = freshForms.find((f) => f.id === id);
          if (updatedForm) {
            setSelectedForm(updatedForm);
          }
        }
      } catch (error) {
        console.error('Error updating form:', error);
      }
    },
    [update, selectedForm, refreshForms]
  );

  const handleDeleteFromPanel = useCallback(
    async (id: string) => {
      if (selectedForm) {
        entity.handleDeleteClick(id, selectedForm.name);
      }
    },
    [selectedForm, entity]
  );

  // Vérifier si des actions sont possibles
  const hasAnyBulkAction = canDeleteBulk('form') || canExportData('form');

  const columns: ColumnDef<Form>[] = useMemo(
    () => [
      // Colonne checkbox uniquement si actions de masse possibles
      ...(hasAnyBulkAction
        ? ([
            {
              id: 'select',
              size: 60,
              header: ({ table }: any) => (
                <CheckboxCell
                  row={
                    {
                      getIsSelected: () => table.getIsAllRowsSelected(),
                      toggleSelected: () => table.toggleAllRowsSelected(),
                    } as any
                  }
                />
              ),
              cell: ({ row }: any) => <CheckboxCell row={row} />,
            },
          ] as ColumnDef<Form>[])
        : []),
      {
        accessorKey: 'name',
        header: 'Nom',
        cell: ({ getValue }) => (
          <TableCell className="font-semibold">{getValue() as string}</TableCell>
        ),
        enableSorting: true,
      },
      {
        id: 'product',
        accessorKey: 'product',
        header: 'Produit',
        cell: ({ row }) => {
          const product = row.original.product;
          return product ? <TableCell>{product.name}</TableCell> : <EmptyCell value={null} />;
        },
      },
      {
        id: 'origin',
        accessorKey: 'origin.label',
        header: 'Origine',
        cell: ({ row }) => {
          const originLabel = row.original.origin?.label;
          return originLabel ? <TableCell>{originLabel}</TableCell> : <EmptyCell value={null} />;
        },
      },
      {
        accessorKey: 'is_active',
        header: 'Statut',
        cell: ({ row }) => {
          const isActive = row.original.is_active;
          return (
            <Badge variant={isActive ? 'default' : 'secondary'}>
              {isActive ? 'Actif' : 'Inactif'}
            </Badge>
          );
        },
      },
      // Colonnes additionnelles (masquées par défaut, disponibles dans le sélecteur)
      {
        accessorKey: 'created_at',
        header: 'Date de création',
        cell: ({ getValue }) => {
          const value = getValue() as string | null;
          if (!value) return <EmptyCell value={null} />;
          return <TableCell>{new Date(value).toLocaleDateString('fr-FR')}</TableCell>;
        },
        size: 150,
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <ActionsCell
            onView={() => handleOpenSidePanel(row.original)}
            onDelete={() => handleDeleteClick(row.original.id, row.original.name)}
            canDelete={canDeleteEntity('form')}
          />
        ),
        size: 120,
      },
    ],
    [handleOpenSidePanel, handleDeleteClick, canDeleteEntity, hasAnyBulkAction]
  );

  // Colonnes disponibles pour la sélection (générées automatiquement)
  const columnOptions = useMemo(
    () => generateColumnOptions(FORM_CONFIG, entity.table.columnVisibility),
    [entity.table.columnVisibility]
  );

  if (entity.loading) {
    return (
      <PageLayout>
        <PageHeader breadcrumbs={[{ label: 'Data' }, { label: 'Formulaires' }]} />
        <div className="w-full h-px bg-zinc-200" />
        <LoadingState message="Chargement des formulaires..." />
      </PageLayout>
    );
  }

  if (entity.error) {
    return (
      <PageLayout>
        <PageHeader breadcrumbs={[{ label: 'Data' }, { label: 'Formulaires' }]} />
        <div className="w-full h-px bg-zinc-200" />
        <ErrorState
          title="Erreur lors du chargement des formulaires"
          message={entity.error.message}
          onRetry={() => window.location.reload()}
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout
      header={
        <>
          <PageHeader breadcrumbs={[{ label: 'Data' }, { label: 'Formulaires' }]} />
          <div className="w-full h-px bg-zinc-200" />
        </>
      }
      sidePanel={
        selectedForm && (
          <FormDetailPanel
            isOpen={isSidePanelOpen}
            onClose={handleCloseSidePanel}
            form={selectedForm}
            onUpdate={handleUpdateForm}
            onDelete={handleDeleteFromPanel}
          />
        )
      }
    >
      <PageContent
        title="Formulaires"
        description="Gérez vos formulaires personnalisables"
        actions={
          <ActionButtons
            searchValue={entity.table.searchTerm}
            onSearchChange={entity.table.setSearchTerm}
            onDownload={canExportData('form') ? entity.handleExportCSV : undefined}
            onUpload={canImportData('form') ? () => entity.setIsImportModalOpen(true) : undefined}
            onFilter={() => entity.setIsFiltersModalOpen(true)}
            onColumns={() => entity.setIsColumnModalOpen(true)}
            onCreate={canCreateEntity('form') ? () => entity.setIsCreateModalOpen(true) : undefined}
            activeFiltersCount={entity.table.activeFilters.length}
          />
        }
        footer={
          <Pagination
            currentPage={entity.table.currentPage}
            totalPages={entity.table.totalPages}
            itemsPerPage={entity.table.itemsPerPage}
            totalItems={entity.table.totalItems}
            displayedItems={entity.table.displayedItemsCount}
            onPageChange={entity.table.setCurrentPage}
            onItemsPerPageChange={entity.table.setItemsPerPage}
            itemsPerPageOptions={[10, 25, 50, 100]}
          />
        }
      >
        <EnterpriseTable
          data={entity.table.filteredData}
          columns={columns}
          onRowSelectionChange={entity.table.setSelectedItems}
          columnVisibility={entity.table.columnVisibility}
          onColumnVisibilityChange={entity.table.setColumnVisibility}
          clearSelectionTrigger={entity.table.clearSelectionTrigger}
          pageSize={entity.table.itemsPerPage}
          currentPage={entity.table.currentPage}
          onPageChange={entity.table.setCurrentPage}
        />
      </PageContent>

      <ColumnSelectorModal
        isOpen={entity.isColumnModalOpen}
        onClose={() => entity.setIsColumnModalOpen(false)}
        columns={columnOptions}
        onApply={(newColumns) => {
          const newVisibility: Record<string, boolean> = {};
          newColumns.forEach((col) => {
            newVisibility[col.id] = col.visible !== false;
          });
          entity.table.setColumnVisibility(newVisibility);
        }}
      />

      <FiltersModal
        isOpen={entity.isFiltersModalOpen}
        onClose={() => entity.setIsFiltersModalOpen(false)}
        filterConfigs={entity.filterConfigs}
        activeFilters={entity.table.activeFilters}
        activeFilterMode={entity.table.filterMode}
        onApply={entity.table.setActiveFilters}
      />

      {/* ✅ OPTIMISATION: Monter la modale uniquement quand elle est ouverte */}
      {entity.isCreateModalOpen && (
        <CreateFormModal
          isOpen={entity.isCreateModalOpen}
          onClose={() => entity.setIsCreateModalOpen(false)}
          onSubmit={async (data) => {
            await entity.handleCreate(data);
          }}
        />
      )}

      <ConfirmDialog
        open={entity.deleteConfirm.open}
        onOpenChange={(open) => entity.setDeleteConfirm({ open })}
        title={
          entity.deleteConfirm.isBulk
            ? `Supprimer les ${FORM_CONFIG.entityNamePlural}`
            : `Supprimer le ${FORM_CONFIG.entityName}`
        }
        description={
          entity.deleteConfirm.isBulk
            ? `Êtes-vous sûr de vouloir supprimer ${entity.table.selectedItems.length} ${FORM_CONFIG.entityName}(s) ?`
            : `Êtes-vous sûr de vouloir supprimer "${entity.deleteConfirm.name}" ?`
        }
        confirmLabel="Supprimer"
        variant="destructive"
        onConfirm={entity.confirmDelete}
      />

      <ImportCSVModal
        open={entity.isImportModalOpen}
        onOpenChange={entity.setIsImportModalOpen}
        config={importConfig}
        mode={entity.importMode}
        onCreate={async (data) => {
          const { data: form, error: supabaseError } = await supabase
            .from('forms')
            .insert([data as any])
            .select()
            .single();

          if (supabaseError) {
            throw supabaseError;
          }

          return form;
        }}
        onUpdate={update}
        existingData={forms}
        entityName={FORM_CONFIG.entityName}
        entityNamePlural={FORM_CONFIG.entityNamePlural}
        onSuccess={entity.handleImportSuccess}
      />

      {/* Barre d'actions de masse */}
      {entity.table.selectedItems.length > 0 && (
        <BulkActionsBar
          count={entity.table.selectedItems.length}
          entityNamePlural={FORM_CONFIG.entityNamePlural}
          onDelete={entity.handleBulkDeleteClick}
          onExportCSV={entity.handleExportSelectedCSV}
          onClearSelection={entity.table.clearSelection}
          showEmailAction={false}
          canDelete={canDeleteBulk('form')}
          canExport={canExportData('form')}
        />
      )}
    </PageLayout>
  );
}
